import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import ClientSideConfigSelector from '../../../recoil/cashout/ClientSideConfigSelector';
import AccessInfoSelector from '../../../recoil/selectors/AccessInfoSelector';
import Loading from '../../general/Loading';

const DEFAULT_TERMS_LANGUAGE = 'en';
const TERMS_BASE_PATH = '/terms';

const normalizeLanguage = (language = 'en') => language.toLowerCase().split('-')[0];

const resolveLocalPath = (network, language) => (
  `${TERMS_BASE_PATH}/${network}/${normalizeLanguage(language)}.html`
);

const resolveLocalPaths = (network, language) => {
  const requestedPath = resolveLocalPath(network, language);
  const fallbackPath = resolveLocalPath(network, DEFAULT_TERMS_LANGUAGE);

  return requestedPath === fallbackPath
    ? [requestedPath]
    : [requestedPath, fallbackPath];
};

const resolveLocalHtml = async (paths) => {
  const attempts = await Promise.all(
    paths.map(async (path) => {
      try {
        const response = await fetch(path, { credentials: 'include' });
        if (!response.ok) {
          return null;
        }
        return response.text();
      } catch (e) {
        return null;
      }
    }),
  );

  return attempts.find(Boolean) || null;
};

export default function CashoutTerms() {
  const { t, i18n } = useTranslation(null, { useSuspense: false });
  const language = i18n?.language || 'en';

  const { network } = useRecoilValue(AccessInfoSelector);
  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
        providers: {
          [paymentProvider]: {
            display: providerDisplay,
          },
        },
      } = {},
    } = {},
  } = config || {};

  const [termsHtml, setTermsHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadTerms = async () => {
      setLoading(true);
      setTermsHtml(null);

      const paths = resolveLocalPaths(network, language);

      try {
        const html = await resolveLocalHtml(paths);
        if (mounted && html) {
          setTermsHtml(html);
        }
      } catch (e) {
        if (mounted) {
          setTermsHtml(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTerms();

    return () => {
      mounted = false;
    };
  }, [network, language]);

  if (loading) {
    return <Loading />;
  }

  if (termsHtml) {
    return (
      <div className="member-diamonds-educate">
        {/* Local network/locale-specific terms loaded from public/terms. */}
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
      </div>
    );
  }

  return (
    <div className="member-diamonds-educate">
      <h4>{t('terms_window_title')}</h4>
      <p>{t('terms_section1')}</p>
      <p>{t('terms_section2', { provider: providerDisplay })}</p>
      <p>{t('terms_section3')}</p>
      <p>{t('terms_section4')}</p>
    </div>
  );
}
